import React, { createContext, useContext, useState, useEffect } from "react";
import { FALLBACK_PRODUCTS, FALLBACK_CATEGORIES } from "../data/fallbackProducts";

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || process.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY;

const PLACEHOLDER_IMAGE = "https://images.pexels.com/photos/6044266/pexels-photo-6044266.jpeg?auto=compress&cs=tinysrgb&w=800&h=1000&fit=crop";

const CACHE_KEY = "products_cache";

function saveToCache(products, categories) {
	try {
		localStorage.setItem(CACHE_KEY, JSON.stringify({ products, categories, timestamp: Date.now() }));
	} catch (_) {}
}

function loadFromCache() {
	try {
		const raw = localStorage.getItem(CACHE_KEY);
		if (!raw) return null;
		return JSON.parse(raw);
	} catch (_) {
		return null;
	}
}

const ProductContext = createContext();

export const useProducts = () => {
	const context = useContext(ProductContext);
	if (!context) {
		throw new Error("useProducts must be used within a ProductProvider");
	}
	return context;
};

const enrichProducts = (rawProducts) =>
	rawProducts.map((p) => {
		const item = {
			...p,
			images: p.images && p.images.length > 0 ? p.images : [PLACEHOLDER_IMAGE],
		};
		if (item.title === "Wonderland Tea Set Basket" && (!item.details || item.details.length === 0)) {
			item.details = [
				"Includes ceramic teapot, 2 cups & saucers",
				"Assorted premium loose-leaf teas",
				"Handmade shortbread cookies",
				"Wrapped in a keepsake wicker basket",
				"Finished with satin ribbon & dried florals",
			];
		}
		return item;
	});

export const ProductProvider = ({ children }) => {
	const [products, setProducts] = useState([]);
	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [usingCache, setUsingCache] = useState(false);

	useEffect(() => {
		const fetchProducts = async () => {
			try {
				if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
					throw new Error("Supabase configuration missing");
				}

				const controller = new AbortController();
				const timeout = setTimeout(() => controller.abort(), 10000);

				const response = await fetch(`${SUPABASE_URL}/functions/v1/stripe-products`, {
					headers: {
						"Authorization": `Bearer ${SUPABASE_ANON_KEY}`,
						"Content-Type": "application/json",
						"apikey": SUPABASE_ANON_KEY,
					},
					signal: controller.signal,
				});
				clearTimeout(timeout);

				if (!response.ok) {
					throw new Error("Failed to fetch products");
				}

				const contentType = response.headers.get("content-type") || "";
				if (!contentType.includes("application/json")) {
					throw new Error("Invalid response from server");
				}

				const data = await response.json();
				const enriched = enrichProducts(data.products || []);
				const cats = data.categories || [];

				setProducts(enriched);
				setCategories(cats);
				saveToCache(enriched, cats);
			} catch (err) {
				console.error("Error fetching products:", err);

				const cached = loadFromCache();
				if (cached && cached.products && cached.products.length > 0) {
					console.log("Using cached products from", new Date(cached.timestamp).toLocaleString());
					setProducts(cached.products);
					setCategories(cached.categories || []);
					setUsingCache(true);
				} else {
					console.log("Using static fallback products");
					setProducts(enrichProducts(FALLBACK_PRODUCTS));
					setCategories(FALLBACK_CATEGORIES);
					setUsingCache(true);
				}
			} finally {
				setLoading(false);
			}
		};

		fetchProducts();
	}, []);

	const getProductsByCategory = (categorySlug) => {
		return products.filter((p) => p.category === categorySlug);
	};

	const getProductBySlug = (slug) => {
		return products.find((p) => p.slug === slug);
	};

	const getAllProducts = () => products;

	return (
		<ProductContext.Provider
			value={{
				products,
				categories,
				loading,
				error,
				usingCache,
				getProductsByCategory,
				getProductBySlug,
				getAllProducts,
			}}
		>
			{children}
		</ProductContext.Provider>
	);
};
